/**
 * Media capture and the upload pipeline.
 *
 * capture → prepare (resize, hash, thumbnail) → Blob in IndexedDB → upload:
 *   1. `POST uploads/init` registers the asset (id generated on the device) and
 *      returns a presigned PUT;
 *   2. the bytes go straight to object storage (cross-origin: no cookies);
 *   3. `POST uploads/{id}/complete` asks the server to confirm what arrived.
 *      404 means "not visible yet" — retried shortly, never treated as lost.
 *
 * Each step is recorded, so a retry resumes where it stopped. An item waits
 * while the record it belongs to has not reached the server yet (its outbox
 * create is still queued): metadata first, bytes second.
 */
import {
  completeUpload as completeUploadApi,
  initUpload as initUploadApi,
  type UploadInitInput,
} from '@/lib/api/field';
import type { MediaEntityType, MediaKind } from '@/lib/api/field-types';
import { isApiError } from '@/lib/api/errors';
import { db, type MediaQueueRecord } from '@/lib/db/db';
import { setServerCopy } from '@/lib/db/field';
import {
  enqueueMedia,
  notifyMediaQueue,
  patchMedia,
  pruneUploadedMedia,
  uploadCandidates,
} from '@/lib/db/media-queue';
import { uuidv7 } from '@/lib/id';

import { heldForWifi } from './network';
import { prepareMedia, quickLocation, sha256Hex, type PrepareOptions } from './prepare';

export type CaptureTarget = {
  projectId: string;
  entityType: MediaEntityType;
  entityId: string;
};

/** Store a picked/captured file for upload. Returns the asset id. */
export async function captureMedia(
  target: CaptureTarget,
  file: File,
  options: PrepareOptions & { caption?: string; locate?: boolean; id?: string } = {},
): Promise<string> {
  const prepared = await prepareMedia(file, options);
  const location = options.locate === false ? null : await quickLocation();
  // A record may reference its photo before the bytes are stored (attendance).
  const id = options.id ?? uuidv7();
  await enqueueMedia({
    id,
    project_id: target.projectId,
    entity_type: target.entityType,
    entity_id: target.entityId,
    kind: prepared.kind,
    blob: prepared.blob,
    thumbnail: prepared.thumbnail,
    mime_type: prepared.mime_type,
    size: prepared.size,
    sha256: prepared.sha256,
    file_name: prepared.file_name,
    caption: options.caption ?? '',
    captured_at_device: new Date(file.lastModified || Date.now()).toISOString(),
    latitude: location?.latitude ?? null,
    longitude: location?.longitude ?? null,
    gps_accuracy_m: location?.accuracy ?? null,
    state: 'queued',
    step: 'init',
    attempts: 0,
    next_attempt_at: null,
    force: false,
    created_at: new Date().toISOString(),
    last_error: null,
  });
  return id;
}

/**
 * Store a drawn signature (PNG from the signature pad) as a `signature`
 * asset. It is kept as drawn — never resized or re-encoded — and its id is
 * chosen by the caller so the record can reference it before it uploads.
 */
export async function captureSignature(
  target: CaptureTarget,
  png: Blob,
  id: string = uuidv7(),
): Promise<string> {
  await enqueueMedia({
    id,
    project_id: target.projectId,
    entity_type: target.entityType,
    entity_id: target.entityId,
    kind: 'signature',
    blob: png,
    thumbnail: png,
    mime_type: 'image/png',
    size: png.size,
    sha256: await sha256Hex(png),
    file_name: 'signature.png',
    caption: '',
    captured_at_device: new Date().toISOString(),
    latitude: null,
    longitude: null,
    gps_accuracy_m: null,
    state: 'queued',
    step: 'init',
    attempts: 0,
    next_attempt_at: null,
    force: false,
    created_at: new Date().toISOString(),
    last_error: null,
  });
  return id;
}

export type UploaderDeps = {
  init: typeof initUploadApi;
  complete: typeof completeUploadApi;
  put: (url: string, blob: Blob, headers: Record<string, string>) => Promise<void>;
  now: () => number;
  held: (item: MediaQueueRecord) => boolean;
};

/** A presigned PUT that did not go through (`network`: never reached storage). */
export class PutFailed extends Error {
  constructor(
    readonly status: number,
    readonly network: boolean,
  ) {
    super(network ? 'Upload interrupted' : `Storage refused the upload (${status})`);
  }
}

/** PUT bytes to a presigned object-storage URL (another origin: no cookies, no token). */
export async function putToStorage(url: string, blob: Blob, headers: Record<string, string>) {
  let response: Response;
  try {
    // Object storage is another origin: never send cookies or the API token.
    response = await fetch(url, { method: 'PUT', body: blob, headers, credentials: 'omit' });
  } catch {
    throw new PutFailed(0, true);
  }
  if (!response.ok) throw new PutFailed(response.status, false);
}

export const defaultUploaderDeps: UploaderDeps = {
  init: initUploadApi,
  complete: completeUploadApi,
  put: putToStorage,
  now: Date.now,
  held: (item) => heldForWifi(item),
};

const BACKOFF_MS = [10_000, 60_000, 5 * 60_000, 30 * 60_000];
/** Storage is eventually consistent: "not arrived yet" is retried quickly. */
const NOT_ARRIVED_RETRY_MS = 5_000;

class Offline extends Error {}

async function parentStillQueued(item: MediaQueueRecord): Promise<boolean> {
  if (item.entity_type === 'project') return false;
  const rows = await db.outbox
    .where('[entity_type+entity_id]')
    .equals([item.entity_type, item.entity_id])
    .toArray();
  return rows.some((row) => row.operation === 'create' && row.state !== 'applied');
}

function initInput(item: MediaQueueRecord): UploadInitInput {
  return {
    asset_id: item.id,
    kind: item.kind as MediaKind,
    content_type: item.mime_type,
    size_bytes: item.size,
    sha256: item.sha256,
    entity_type: item.entity_type,
    entity_id: item.entity_id,
    original_filename: item.file_name,
    caption: item.caption,
    captured_at_device: item.captured_at_device,
    latitude: item.latitude,
    longitude: item.longitude,
    gps_accuracy_m: item.gps_accuracy_m,
  };
}

async function uploadOne(item: MediaQueueRecord, deps: UploaderDeps): Promise<boolean> {
  await patchMedia(item.id, { state: 'uploading' });
  let step = item.step;
  try {
    if (step === 'init' || step === 'put') {
      if (!item.blob) throw new Error('The file is no longer on this device.');
      let target: { url: string; headers: Record<string, string> } | null = null;
      try {
        // init is idempotent on the asset id; a resumed PUT just gets a fresh URL.
        const { upload } = await deps.init(item.project_id, initInput(item));
        target = upload;
      } catch (error) {
        // Already uploaded on an earlier attempt: go straight to confirming.
        if (!(isApiError(error) && error.code === 'INVALID_STATE_TRANSITION')) throw error;
      }
      if (target) {
        step = 'put';
        await patchMedia(item.id, { step });
        await deps.put(target.url, item.blob, target.headers);
      }
      step = 'complete';
      await patchMedia(item.id, { step });
    }
    const asset = await deps.complete(item.project_id, item.id);
    await db.transaction('rw', [db.media_assets, db.outbox, db.media_queue], async () => {
      await setServerCopy(
        'media_asset',
        asset as unknown as Record<string, unknown>,
        asset.version,
      );
      await db.media_queue.update(item.id, {
        state: 'uploaded',
        step: 'done',
        blob: null,
        last_error: null,
        next_attempt_at: null,
      });
    });
    notifyMediaQueue();
    return true;
  } catch (error) {
    const network =
      (isApiError(error) && error.isNetwork) || (error instanceof PutFailed && error.network);
    if (network) {
      await patchMedia(item.id, { state: 'queued', step });
      throw new Offline();
    }
    const attempts = item.attempts + 1;
    const notArrived = isApiError(error) && error.status === 404 && step === 'complete';
    const retryable = notArrived || (isApiError(error) ? error.isRetryable : true);
    const delay = notArrived
      ? NOT_ARRIVED_RETRY_MS
      : (BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)] ?? 0);
    await patchMedia(item.id, {
      state: 'failed',
      step,
      attempts,
      // Non-retryable (rejected type/size): wait for a person to retry or remove it.
      next_attempt_at: retryable ? deps.now() + delay : null,
      last_error: notArrived
        ? 'Waiting for storage to confirm the upload'
        : error instanceof Error
          ? error.message
          : 'Upload failed',
    });
    return false;
  }
}

let running: Promise<{ uploaded: number }> | null = null;

/** Upload what can go now. Safe to call often; concurrent calls share one run. */
export function processMediaQueue(
  deps: UploaderDeps = defaultUploaderDeps,
): Promise<{ uploaded: number }> {
  running ??= (async () => {
    let uploaded = 0;
    try {
      for (const item of await uploadCandidates()) {
        const due =
          item.state === 'queued' ||
          item.state === 'uploading' ||
          (item.state === 'failed' &&
            item.next_attempt_at !== null &&
            item.next_attempt_at <= deps.now());
        if (!due || deps.held(item) || (await parentStillQueued(item))) continue;
        if (await uploadOne(item, deps)) uploaded += 1;
      }
      await pruneUploadedMedia();
    } catch (error) {
      if (!(error instanceof Offline)) throw error;
    } finally {
      running = null;
    }
    return { uploaded };
  })();
  return running;
}
